/**
 * Shop AI Chat - Client-side implementation
 *
 * This module handles the chat interface for the Shopify AI Chat application.
 * It manages the UI interactions, API communication, and message rendering.
 */
(function() {
  'use strict';

  /**
   * Application namespace to prevent global scope pollution
   */
  const ShopAIChat = {
    /**
     * UI-related elements and functionality
     */
    UI: {
      elements: {},
      isMobile: false,

      /**
       * Initialize UI elements and event listeners
       * @param {HTMLElement} container - The main container element
       */
      init: function(container) {
        if (!container) return;

        // Cache DOM elements
        this.elements = {
          container: container,
          chatBubble: container.querySelector('.shop-ai-chat-bubble'),
          chatWindow: container.querySelector('.shop-ai-chat-window'),
          closeButton: container.querySelector('.shop-ai-chat-close'),
          chatInput: container.querySelector('.shop-ai-chat-input input'),
          sendButton: container.querySelector('.shop-ai-chat-send'),
          messagesContainer: container.querySelector('.shop-ai-chat-messages')
        };

        // Detect mobile device
        this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        // ACCESSIBILITY (additive): mark the message stream as a polite live region so
        // screen readers announce streamed assistant replies, quiz cards, and product
        // results as they arrive. role="log" gives it the right semantics; aria-busy is
        // toggled by setBusy() during streaming. No markup change required in the snippet.
        if (this.elements.messagesContainer) {
          const mc = this.elements.messagesContainer;
          if (!mc.getAttribute('role')) mc.setAttribute('role', 'log');
          if (!mc.getAttribute('aria-live')) mc.setAttribute('aria-live', 'polite');
          mc.setAttribute('aria-relevant', 'additions text');
          mc.setAttribute('aria-atomic', 'false');
          mc.setAttribute('aria-busy', 'false');
        }

        // Set up event listeners
        this.setupEventListeners();

        // Fix for iOS Safari viewport height issues
        if (this.isMobile) {
          this.setupMobileViewport();
        }
      },

      /**
       * Set up all event listeners for UI interactions
       */
      setupEventListeners: function() {
        const { chatBubble, closeButton, chatInput, sendButton, messagesContainer } = this.elements;

        // Toggle chat window visibility
        chatBubble.addEventListener('click', () => this.toggleChatWindow());
        // Keyboard access for the launcher (the liquid gives it role=button tabindex=0)
        chatBubble.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.toggleChatWindow(); }
        });

        // Close chat window
        closeButton.addEventListener('click', () => this.closeChatWindow());

        // Send message when pressing Enter in input
        chatInput.addEventListener('keypress', (e) => {
          // Don't let Enter bypass the disabled send button mid-stream (double-send guard).
          if (e.key === 'Enter' && sendButton && sendButton.disabled) { e.preventDefault(); return; }
          if (e.key === 'Enter' && chatInput.value.trim() !== '') {
            ShopAIChat.Message.send(chatInput, messagesContainer);

            // On mobile, handle keyboard
            if (this.isMobile) {
              chatInput.blur();
              setTimeout(() => chatInput.focus(), 300);
            }
          }
        });

        // Send message when clicking send button
        sendButton.addEventListener('click', () => {
          if (chatInput.value.trim() !== '') {
            ShopAIChat.Message.send(chatInput, messagesContainer);

            // On mobile, focus input after sending
            if (this.isMobile) {
              setTimeout(() => chatInput.focus(), 300);
            }
          }
        });

        // Handle window resize to adjust scrolling
        window.addEventListener('resize', () => this.scrollToBottom());

        // Reader-intent tracking for the scroll guard: _pinned means "at/near the bottom
        // the last time anyone (reader or us) actually scrolled". Content appends don't
        // fire scroll events, so this survives big appends (product grids, finale prose) —
        // measuring the gap after an append cannot tell "reader scrolled up" from "tall
        // content just landed".
        this._pinned = true;
        messagesContainer.addEventListener('scroll', () => {
          const gap = messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight;
          this._pinned = gap <= 140;
        });

        // Add global click handler for auth links
        document.addEventListener('click', function(event) {
          if (event.target && event.target.classList.contains('shop-auth-trigger')) {
            event.preventDefault();
            if (window.shopAuthUrl) {
              ShopAIChat.Auth.openAuthPopup(window.shopAuthUrl);
            }
          }
        });
      },

      /**
       * Setup mobile-specific viewport adjustments
       */
      setupMobileViewport: function() {
        const setViewportHeight = () => {
          document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        };
        window.addEventListener('resize', setViewportHeight);
        setViewportHeight();
      },

      /**
       * Toggle chat window visibility
       */
      toggleChatWindow: function() {
        const { chatWindow, chatInput, chatBubble } = this.elements;

        chatWindow.classList.toggle('active');

        if (chatWindow.classList.contains('active')) {
          // Opening the chat pauses the nudge for this page-load only;
          // only the explicit ✕ dismiss silences it for the whole session.
          if (ShopAIChat.Nudge) ShopAIChat.Nudge.stop();
          // Hide the launcher while the window is open (it overlapped the input on mobile).
          if (chatBubble) chatBubble.classList.add('shop-ai-bubble-hidden');
          ShopAIChat.Analytics.push('chat_opened');
          // On mobile, prevent body scrolling and delay focus
          if (this.isMobile) {
            document.body.classList.add('shop-ai-chat-open');
            setTimeout(() => chatInput.focus(), 500);
          } else {
            chatInput.focus();
          }
          // Always scroll messages to bottom when opening
          this.scrollToBottom(true);
        } else {
          if (chatBubble) chatBubble.classList.remove('shop-ai-bubble-hidden');
          // Remove body class when closing
          document.body.classList.remove('shop-ai-chat-open');
        }
      },

      /**
       * Close chat window
       */
      closeChatWindow: function() {
        const { chatWindow, chatInput, chatBubble } = this.elements;

        chatWindow.classList.remove('active');
        if (chatBubble) chatBubble.classList.remove('shop-ai-bubble-hidden');

        // On mobile, blur input to hide keyboard and enable body scrolling
        if (this.isMobile) {
          chatInput.blur();
          document.body.classList.remove('shop-ai-chat-open');
        }
      },

      /**
       * Scroll messages container to bottom.
       * @param {boolean} force - true for user-initiated moments (open, send, form/quiz shown):
       * always scroll. Streaming/layout events stay guarded: if the reader deliberately
       * scrolled up (see _pinned), don't yank them back down.
       */
      scrollToBottom: function(force) {
        const { messagesContainer } = this.elements;
        setTimeout(() => {
          if (!force && this._pinned === false) return;
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }, 100);
      },

      /**
       * Toggle the "streaming in progress" state on the composer.
       * ADDITIVE: disables + spins the send button while a reply streams so the
       * shopper can't double-send (which would interleave two SSE streams into
       * the same message container). Also flags the live region as busy for AT.
       * Idempotent and defensive — never throws, never blocks the stream.
       * @param {boolean} busy
       */
      setBusy: function(busy) {
        try {
          const send = this.elements.sendButton;
          const input = this.elements.chatInput;
          const messages = this.elements.messagesContainer;
          if (send) {
            send.classList.toggle('is-busy', !!busy);
            send.disabled = !!busy;
            send.setAttribute('aria-busy', busy ? 'true' : 'false');
          }
          // Keep the input usable for typing the next question, but reflect state.
          if (input) input.classList.toggle('shop-ai-input-busy', !!busy);
          if (messages) messages.setAttribute('aria-busy', busy ? 'true' : 'false');
        } catch (e) { /* non-fatal */ }
      },

      /**
       * Show typing indicator in the chat
       */
      showTypingIndicator: function() {
        const { messagesContainer } = this.elements;

        const typingIndicator = document.createElement('div');
        typingIndicator.classList.add('shop-ai-typing-indicator');
        typingIndicator.innerHTML = '<span></span><span></span><span></span>';
        messagesContainer.appendChild(typingIndicator);
        this.scrollToBottom();
      },

      /**
       * Remove typing indicator from the chat
       */
      removeTypingIndicator: function() {
        const { messagesContainer } = this.elements;

        const typingIndicator = messagesContainer.querySelector('.shop-ai-typing-indicator');
        if (typingIndicator) {
          typingIndicator.remove();
        }
      },

      /**
       * Display product results in the chat
       * @param {Array} products - Array of product data objects
       */
      displayProductResults: function(products) {
        const { messagesContainer } = this.elements;

        // Create a wrapper for the product section
        const productSection = document.createElement('div');
        productSection.classList.add('shop-ai-product-section');
        // Quiz-finale gate: blur the results and point at the form until it's submitted.
        if (ShopAIChat.Quiz._gateActive) {
          const note = document.createElement('div');
          note.className = 'shop-ai-gate-note';
          note.textContent = String.fromCodePoint(0x1F3AF) + " Your matches are ready. Drop your number above and I'll unlock them now + text you the list so it's saved.";
          messagesContainer.appendChild(note);
          productSection.classList.add('shop-ai-gated');
        }
        messagesContainer.appendChild(productSection);

        // Add a header for the product results
        const header = document.createElement('div');
        header.classList.add('shop-ai-product-header');
        header.innerHTML = '<h4>Top matches for you</h4>';
        productSection.appendChild(header);

        // Create the product grid container
        const productsContainer = document.createElement('div');
        productsContainer.classList.add('shop-ai-product-grid');
        productSection.appendChild(productsContainer);

        if (!products || !Array.isArray(products) || products.length === 0) {
          const noProductsMessage = document.createElement('p');
          noProductsMessage.textContent = "No products found";
          noProductsMessage.style.padding = "10px";
          productsContainer.appendChild(noProductsMessage);
        } else {
          // Pull the just-finished assistant reply so each card can show the bot's own
          // "why it fits you" line. product_results fires around end_turn, so the latest
          // NON-EMPTY assistant bubble holds the complete reply with the per-bed lines
          // (scan from the end, skipping any trailing empty/placeholder bubble).
          const assistantEls = messagesContainer.querySelectorAll('.shop-ai-message.assistant');
          let lastReply = '';
          for (let k = assistantEls.length - 1; k >= 0; k--) {
            const txt = assistantEls[k].dataset.rawText || assistantEls[k].textContent || '';
            if (txt.trim()) { lastReply = txt; break; }
          }
          const reasons = ShopAIChat.Product.extractReasons(lastReply);
          products.forEach(product => {
            const reason = ShopAIChat.Product.reasonFor(product.title, reasons);
            const productCard = ShopAIChat.Product.createCard(product, reason);
            productsContainer.appendChild(productCard);
          });
          // Deterministic capture safety net: with a real shortlist on screen and no lead
          // form anywhere in the thread (active OR submitted), offer to text the picks —
          // shopper-tap-initiated, so it works even when the model never offers.
          if (products.length >= 2 && !sessionStorage.getItem('shopAiLeadDone') && !messagesContainer.querySelector('form.shop-ai-lead-form:not(.shop-ai-support-form)')) {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'shop-ai-text-picks-chip';
            chip.textContent = String.fromCodePoint(0x1F4F2) + ' Text me these picks';
            chip.addEventListener('click', () => {
              ShopAIChat.Analytics.push('chip_text_picks_clicked');
              chip.remove();
              ShopAIChat.Lead.show('save_cart', messagesContainer);
            });
            productSection.appendChild(chip);
          }

          // Carousel scroll affordance (ADDITIVE, visual-only): toggle .is-scrollable
          // on the section once the row actually overflows, and .at-end when scrolled
          // to the end — the CSS shows/retracts the right-edge fade + "→" cue off these.
          // A single card (or a non-overflowing row) never gets .is-scrollable, so it
          // shows no peek/fade. No-op on the mobile vertical stack (no x-overflow).
          this.wireCarouselCue(productSection, productsContainer);
        }

        // Quiz-finale gate: float an HONEST "N matches locked" teaser CENTERED over the
        // blurred matches. N is the REAL number of product cards shown (products.length) —
        // never a fabricated figure. The overlay is a SIBLING inside a .shop-ai-gate-stack
        // wrapper (NOT itself blurred). Additive + visual-only; the blur/unlock flow is
        // untouched. Skipped when no real matches rendered (we never claim a count we can't
        // back up).
        if (ShopAIChat.Quiz._gateActive && Array.isArray(products) && products.length > 0) {
          this.mountGateLockOverlay(productSection, products.length);
        }

        this.scrollToBottom();
      },

      /**
       * Wrap a gated element in a .shop-ai-gate-stack and float a "N matches locked"
       * teaser overlay over it as a (non-blurred) sibling. The count is REAL — it must
       * be the actual number of matches/products shown. Idempotent: re-mounting reuses
       * the existing stack/overlay and just refreshes the count.
       * @param {HTMLElement} gatedEl - the blurred element to overlay (the product section)
       * @param {number} count - the real number of matches shown
       */
      mountGateLockOverlay: function(gatedEl, count) {
        if (!gatedEl) return;
        // Ensure the gated element lives inside a position:relative stack so the
        // absolutely-centered overlay anchors to it. Re-parenting is a local DOM move
        // that preserves the element's identity, classes and dataset.
        let stack = gatedEl.parentElement;
        if (!stack || !stack.classList.contains('shop-ai-gate-stack')) {
          stack = document.createElement('div');
          stack.className = 'shop-ai-gate-stack';
          gatedEl.parentNode.insertBefore(stack, gatedEl);
          stack.appendChild(gatedEl);
        }
        let overlay = stack.querySelector('.shop-ai-lock-overlay');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'shop-ai-lock-overlay';
          overlay.setAttribute('aria-hidden', 'true');
          stack.appendChild(overlay);
        }
        const n = Number(count);
        const hasCount = Number.isFinite(n) && n > 0;
        const lockIcon = String.fromCodePoint(0x1F512);
        if (hasCount) {
          const label = n === 1 ? 'match' : 'matches';
          // textContent-only assembly (no innerHTML) keeps it injection-safe.
          overlay.textContent = '';
          const countLine = document.createElement('div');
          countLine.className = 'shop-ai-lock-count';
          const icon = document.createElement('span');
          icon.className = 'shop-ai-lock-icon';
          icon.textContent = lockIcon;
          const text = document.createElement('span');
          const num = document.createElement('span');
          num.className = 'shop-ai-lock-num';
          num.textContent = n + ' ' + label;
          text.appendChild(num);
          text.appendChild(document.createTextNode(' locked'));
          countLine.appendChild(icon);
          countLine.appendChild(text);
          overlay.appendChild(countLine);
        } else {
          // No reliable count — name the prize without a number (never fabricate one).
          overlay.textContent = '';
          const countLine = document.createElement('div');
          countLine.className = 'shop-ai-lock-count';
          const icon = document.createElement('span');
          icon.className = 'shop-ai-lock-icon';
          icon.textContent = lockIcon;
          const text = document.createElement('span');
          text.textContent = 'Your matches';
          countLine.appendChild(icon);
          countLine.appendChild(text);
          overlay.appendChild(countLine);
        }
      },

      /**
       * Toggle the scroll-affordance classes on a product section based on whether its
       * grid overflows horizontally and where it's scrolled. Purely additive/visual —
       * never alters markup or card behavior.
       * @param {HTMLElement} section - .shop-ai-product-section
       * @param {HTMLElement} grid - .shop-ai-product-grid (the scroll container)
       */
      wireCarouselCue: function(section, grid) {
        if (!section || !grid) return;

        const SLOP = 4; // px tolerance for sub-pixel rounding
        const update = function() {
          const overflow = grid.scrollWidth - grid.clientWidth;
          if (overflow > SLOP) {
            section.classList.add('is-scrollable');
            const atEnd = grid.scrollLeft >= (overflow - SLOP);
            section.classList.toggle('at-end', atEnd);
          } else {
            // Not scrollable (single card / fits) — no cue at all.
            section.classList.remove('is-scrollable');
            section.classList.remove('at-end');
          }
        };

        grid.addEventListener('scroll', update, { passive: true });

        // Re-measure on viewport changes (window resize, mobile rotate). Stored on the
        // grid so it can be cleaned conceptually; harmless if the node is removed.
        if (typeof ResizeObserver === 'function') {
          const ro = new ResizeObserver(update);
          ro.observe(grid);
        } else {
          window.addEventListener('resize', update, { passive: true });
        }

        // Initial measure: now, and again after images load / layout settles.
        update();
        requestAnimationFrame(update);
        setTimeout(update, 120);
      }
    },

    /**
     * Message handling and display functionality
     */
    Message: {
      /**
       * Send a message to the API
       * @param {HTMLInputElement} chatInput - The input element
       * @param {HTMLElement} messagesContainer - The messages container
       */
      send: async function(chatInput, messagesContainer) {
        ShopAIChat.Quiz.removeWelcomeChip();
        const userMessage = chatInput.value.trim();
        const conversationId = sessionStorage.getItem('shopAiConversationId');

        // Add user message to chat
        this.add(userMessage, 'user', messagesContainer);

        // Clear input
        chatInput.value = '';

        // Show typing indicator + lock the composer for the duration of the stream
        ShopAIChat.UI.showTypingIndicator();
        ShopAIChat.UI.setBusy(true);

        try {
          ShopAIChat.API.streamResponse(userMessage, conversationId, messagesContainer);
        } catch (error) {
          console.error('Error communicating with Claude API:', error);
          ShopAIChat.UI.removeTypingIndicator();
          ShopAIChat.UI.setBusy(false);
          this.add("Sorry, I couldn't process your request at the moment. Please try again later.", 'assistant', messagesContainer);
        }
      },

      /**
       * Add a message to the chat
       * @param {string} text - Message content
       * @param {string} sender - Message sender ('user' or 'assistant')
       * @param {HTMLElement} messagesContainer - The messages container
       * @returns {HTMLElement} The created message element
       */
      add: function(text, sender, messagesContainer) {
        const messageElement = document.createElement('div');
        messageElement.classList.add('shop-ai-message', sender);

        if (sender === 'assistant') {
          messageElement.dataset.rawText = text;
          ShopAIChat.Formatting.formatMessageContent(messageElement);
        } else {
          messageElement.textContent = text;
        }

        messagesContainer.appendChild(messageElement);
        ShopAIChat.UI.scrollToBottom(true);

        return messageElement;
      },

      /**
       * Add a tool use message to the chat with expandable arguments
       * @param {string} toolMessage - Tool use message content
       * @param {HTMLElement} messagesContainer - The messages container
       */
      addToolUse: function(toolMessage, messagesContainer) {
        // Parse the tool message to extract tool name and arguments
        const match = toolMessage.match(/Calling tool: (\w+) with arguments: (.+)/);
        if (!match) {
          // Fallback for unexpected format
          const toolUseElement = document.createElement('div');
          toolUseElement.classList.add('shop-ai-message', 'tool-use');
          toolUseElement.textContent = toolMessage;
          messagesContainer.appendChild(toolUseElement);
          ShopAIChat.UI.scrollToBottom();
          return;
        }

        const toolName = match[1];
        const argsString = match[2];

        // Create the main tool use element
        const toolUseElement = document.createElement('div');
        toolUseElement.classList.add('shop-ai-message', 'tool-use');

        // Create the header (always visible)
        const headerElement = document.createElement('div');
        headerElement.classList.add('shop-ai-tool-header');

        const toolText = document.createElement('span');
        toolText.classList.add('shop-ai-tool-text');
        toolText.textContent = `Calling tool: ${toolName}`;

        const toggleElement = document.createElement('span');
        toggleElement.classList.add('shop-ai-tool-toggle');
        toggleElement.textContent = '[+]';

        headerElement.appendChild(toolText);
        headerElement.appendChild(toggleElement);

        // Create the arguments section (initially hidden)
        const argsElement = document.createElement('div');
        argsElement.classList.add('shop-ai-tool-args');

        try {
          // Try to format JSON arguments nicely
          const parsedArgs = JSON.parse(argsString);
          argsElement.textContent = JSON.stringify(parsedArgs, null, 2);
        } catch (e) {
          // If not valid JSON, just show as-is
          argsElement.textContent = argsString;
        }

        // Add click handler to toggle arguments visibility
        headerElement.addEventListener('click', function() {
          const isExpanded = argsElement.classList.contains('expanded');
          if (isExpanded) {
            argsElement.classList.remove('expanded');
            toggleElement.textContent = '[+]';
          } else {
            argsElement.classList.add('expanded');
            toggleElement.textContent = '[-]';
          }
        });

        // Assemble the complete element
        toolUseElement.appendChild(headerElement);
        toolUseElement.appendChild(argsElement);

        messagesContainer.appendChild(toolUseElement);
        ShopAIChat.UI.scrollToBottom();
      }
    },

    /**
     * Text formatting and markdown handling
     */
    Formatting: {
      /**
       * Format message content with markdown and links
       * @param {HTMLElement} element - The element to format
       */
      /** Escape HTML so model/catalog text can never inject markup into innerHTML. */
      escapeHtml: function(s) {
        return String(s == null ? '' : s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      },

      /** Only allow safe link schemes; anything else (javascript:, data:, etc.) -> '#'. */
      safeUrl: function(url) {
        const u = String(url == null ? '' : url).trim();
        return /^(https?:\/\/|\/|mailto:|#)/i.test(u) ? u : '#';
      },

      formatMessageContent: function(element) {
        if (!element || !element.dataset.rawText) return;

        // SECURITY: escape the raw model text FIRST so any HTML it (or injected catalog
        // data) contains is neutralized before we add our own safe markup below.
        let processedText = this.escapeHtml(element.dataset.rawText);

        // Process Markdown links. Link text is already escaped; sanitize the URL scheme.
        const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        processedText = processedText.replace(markdownLinkRegex, (match, text, url) => {
          const safe = this.safeUrl(url);
          if (url.includes('shopify.com/authentication') &&
             (url.includes('oauth/authorize') || url.includes('authentication'))) {
            window.shopAuthUrl = safe;
            return '<a href="#auth" class="shop-auth-trigger">' + text + '</a>';
          } else if (url.includes('/cart') || url.includes('checkout')) {
            return '<a href="' + safe + '" target="_blank" rel="noopener noreferrer">click here to proceed to checkout</a>';
          } else {
            return '<a href="' + safe + '" target="_blank" rel="noopener noreferrer">' + text + '</a>';
          }
        });

        // Convert the (already-escaped) text to HTML with bold/list handling.
        processedText = this.convertMarkdownToHtml(processedText);

        element.innerHTML = processedText;
      },

      /**
       * Convert Markdown text to HTML with list support
       * @param {string} text - Markdown text to convert
       * @returns {string} HTML content
       */
      convertMarkdownToHtml: function(text) {
        text = text.replace(/(\*\*|__)(.*?)\1/g, '<strong>$2</strong>');
        const lines = text.split('\n');
        let currentList = null;
        let listItems = [];
        let htmlContent = '';
        let startNumber = 1;

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const unorderedMatch = line.match(/^\s*([-*])\s+(.*)/);
          const orderedMatch = line.match(/^\s*(\d+)[\.)]\s+(.*)/);

          if (unorderedMatch) {
            if (currentList !== 'ul') {
              if (currentList === 'ol') {
                htmlContent += `<ol start="${startNumber}">` + listItems.join('') + '</ol>';
                listItems = [];
              }
              currentList = 'ul';
            }
            listItems.push('<li>' + unorderedMatch[2] + '</li>');
          } else if (orderedMatch) {
            if (currentList !== 'ol') {
              if (currentList === 'ul') {
                htmlContent += '<ul>' + listItems.join('') + '</ul>';
                listItems = [];
              }
              currentList = 'ol';
              startNumber = parseInt(orderedMatch[1], 10);
            }
            listItems.push('<li>' + orderedMatch[2] + '</li>');
          } else {
            if (currentList) {
              htmlContent += currentList === 'ul'
                ? '<ul>' + listItems.join('') + '</ul>'
                : `<ol start="${startNumber}">` + listItems.join('') + '</ol>';
              listItems = [];
              currentList = null;
            }

            if (line.trim() === '') {
              htmlContent += '<br>';
            } else {
              htmlContent += '<p>' + line + '</p>';
            }
          }
        }

        if (currentList) {
          htmlContent += currentList === 'ul'
            ? '<ul>' + listItems.join('') + '</ul>'
            : `<ol start="${startNumber}">` + listItems.join('') + '</ol>';
        }

        htmlContent = htmlContent.replace(/<\/p><p>/g, '</p>\n<p>');
        return htmlContent;
      }
    },

    PageContext: {
      handle: function() {
        const m = window.location.pathname.match(/\/products\/([a-z0-9-]+)/);
        return m ? m[1] : null;
      },
      shortTitle: function() {
        // "Purple Plus King — Mattress Overstock" -> "Purple Plus King"
        const t = (document.title || '').split(/\s*[|–—-]\s*Mattress Overstock.*$/i)[0].trim();
        return t.length > 1 ? t.slice(0, 48) : null;
      },
      get: function() {
        return {
          url: window.location.href.slice(0, 512),
          title: (document.title || '').slice(0, 256),
          handle: this.handle()
        };
      }
    },

    Cookies: {
      read: function(name) {
        const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : null;
      },
      // First-party analytics ids; stitch server-side events to this browser session.
      collect: function() {
        const out = {};
        try {
          const ga = this.read('_ga'); // "GA1.1.123456.789" -> "123456.789"
          if (ga) { const m = ga.match(/GA\d+\.\d+\.(.+)$/); if (m) out.ga_client_id = m[1].slice(0, 64); }
          const fbp = this.read('_fbp'); if (fbp) out.fbp = fbp.slice(0, 128);
          const fbc = this.read('_fbc'); if (fbc) out.fbc = fbc.slice(0, 256);
        } catch (e) { /* non-fatal */ }
        return out;
      }
    },

    Analytics: {
      // GTM-ready funnel events. No PII, ever.
      push: function(event, params) {
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(Object.assign({ event: event, pdp: !!ShopAIChat.PageContext.handle() }, params || {}));
        } catch (e) { /* non-fatal */ }
      }
    },

    Attribution: {
      KEY: 'shopAiAttribution',
      capture: function() {
        try {
          const p = new URLSearchParams(window.location.search);
          const pick = ['gclid','fbclid','utm_source','utm_medium','utm_campaign','utm_term','utm_content'];
          const cur = {};
          pick.forEach((k) => { const v = p.get(k); if (v) cur[k] = v.slice(0, 256); });
          const existing = this.get();
          // First-touch: only set once; keep the original ad click across in-session navigation.
          if (!existing || Object.keys(existing).length === 0) {
            const data = { ...cur, landing_url: window.location.href.slice(0, 512), referrer: (document.referrer || '').slice(0, 512), ts: Date.now() };
            localStorage.setItem(this.KEY, JSON.stringify(data));
          }
        } catch (e) { /* storage disabled — non-fatal */ }
      },
      get: function() {
        try { return JSON.parse(localStorage.getItem(this.KEY) || '{}'); } catch (e) { return {}; }
      }
    },

    Lead: {
      BACKEND: 'https://mo-shop-chat-agent-production.up.railway.app/lead',
      copy: {
        save_cart:  { title: "I'll text you your picks + today's prices — nothing to remember.", btn: "Text me my picks" },
        price_drop: { title: "Get one text the moment this goes on sale.", btn: "Watch this price" },
        first_dibs: { title: "Be first to hear when exclusive offers go live.", btn: "Count me in" },
        financing:  { title: "Our team will text you real monthly-payment options.", btn: "Text me options" }
      },
      consentText: "I agree to receive recurring automated marketing & cart-reminder texts from Mattress Overstock at the number above. Consent isn't a condition of purchase. Msg & data rates may apply. Reply STOP to opt out, HELP for help.",
      show: function(hook, messagesContainer, opts) {
        // Already captured this session (survives reloads) — never re-offer a form.
        if (sessionStorage.getItem('shopAiLeadDone')) return;
        // Already captured in this thread — never stack another form on a submitted one.
        if (messagesContainer.querySelector('form.shop-ai-lead-form.shop-ai-lead-done')) return;
        const c = this.copy[hook] || this.copy.save_cart;
        // An active (unsubmitted) lead form: re-anchor it under the newest offer instead of
        // silently dropping the offer. (:not(.shop-ai-support-form) — the support form shares
        // the lead-form class and used to block lead forms entirely.)
        const existing = messagesContainer.querySelector('form.shop-ai-lead-form:not(.shop-ai-support-form):not(.shop-ai-lead-done)');
        if (existing) {
          existing.dataset.hook = hook;
          existing.querySelector('.shop-ai-lead-title').textContent = c.title;
          existing.querySelector('.shop-ai-lead-submit').textContent = c.btn;
          messagesContainer.appendChild(existing);
          ShopAIChat.Analytics.push('lead_form_shown', { hook: hook, reshown: true });
          try { sessionStorage.setItem('shopAiPendingLeadHook', hook); } catch (e) { /* non-fatal */ }
          ShopAIChat.UI.scrollToBottom(true);
          return;
        }
        // A form restored after a page reload is NOT a new offer — don't re-count it in
        // the GA4 funnel denominator (one ignoring shopper browsing 5 PDPs is 1 offer, not 6).
        if (!(opts && opts.restored)) {
          ShopAIChat.Analytics.push('lead_form_shown', { hook: hook });
        }
        const form = document.createElement('form');
        form.className = 'shop-ai-lead-form';
        form.dataset.hook = hook;
        form.innerHTML =
          '<div class="shop-ai-lead-title"></div>' +
          '<input class="shop-ai-lead-name" type="text" placeholder="First name (optional)" autocomplete="given-name">' +
          '<input class="shop-ai-lead-phone" type="tel" placeholder="Mobile number" autocomplete="tel">' +
          '<button type="button" class="shop-ai-lead-email-toggle">Prefer email instead?</button>' +
          '<input class="shop-ai-lead-email shop-ai-lead-email-hidden" type="email" placeholder="Email" autocomplete="email">' +
          '<label class="shop-ai-lead-consent"><input type="checkbox" class="shop-ai-lead-consent-box"> <span></span></label>' +
          '<div class="shop-ai-lead-hint" role="status"></div>' +
          '<div class="shop-ai-lead-trust"></div>' +
          '<button type="submit" class="shop-ai-lead-submit"></button>' +
          '<div class="shop-ai-lead-status" role="status"></div>';
        form.querySelector('.shop-ai-lead-title').textContent = c.title;
        form.querySelector('.shop-ai-lead-consent span').textContent = this.consentText;
        form.querySelector('.shop-ai-lead-submit').textContent = c.btn;
        // Trust microcopy — honest, claim-free friction-reducer at the decision point.
        // textContent-only assembly with dot separators keeps it injection-safe.
        (function (trust) {
          if (!trust) return;
          const dot = function () {
            const d = document.createElement('span');
            d.className = 'shop-ai-trust-dot';
            return d;
          };
          trust.appendChild(document.createTextNode(String.fromCodePoint(0x1F512) + ' We never share your number'));
          trust.appendChild(dot());
          trust.appendChild(document.createTextNode('unsubscribe anytime'));
          trust.appendChild(dot());
          trust.appendChild(document.createTextNode('no spam'));
        })(form.querySelector('.shop-ai-lead-trust'));
        form.querySelector('.shop-ai-lead-email-toggle').addEventListener('click', () => {
          const emailInput = form.querySelector('.shop-ai-lead-email');
          emailInput.classList.remove('shop-ai-lead-email-hidden');
          form.querySelector('.shop-ai-lead-email-toggle').remove();
          emailInput.focus();
        });
        form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(form, form.dataset.hook || hook); });
        // Survive full page reloads (every product-card click reloads the page) — see fetchChatHistory.
        try { sessionStorage.setItem('shopAiPendingLeadHook', hook); } catch (e) { /* non-fatal */ }
        messagesContainer.appendChild(form);
        ShopAIChat.UI.scrollToBottom(true);
      },
      submit: async function(form, hook) {
        const name = form.querySelector('.shop-ai-lead-name').value.trim();
        const phone = form.querySelector('.shop-ai-lead-phone').value.trim();
        const email = form.querySelector('.shop-ai-lead-email').value.trim();
        const consent = form.querySelector('.shop-ai-lead-consent-box').checked;
        const status = form.querySelector('.shop-ai-lead-status');
        const btn = form.querySelector('.shop-ai-lead-submit');
        const hint = form.querySelector('.shop-ai-lead-hint');
        const consentLabel = form.querySelector('.shop-ai-lead-consent');
        if (hint) hint.textContent = '';
        if (consentLabel) consentLabel.classList.remove('shop-ai-consent-attention');
        if (!phone && !email) { status.textContent = 'Please add a phone or email.'; return; }
        if (phone && !consent) {
          // Point at the fix instead of dead-ending at the bottom of the form.
          if (hint) hint.textContent = 'Check the box above so we can legally text you.';
          if (consentLabel) consentLabel.classList.add('shop-ai-consent-attention');
          status.textContent = '';
          return;
        }
        btn.disabled = true; status.textContent = 'Sending…';
        try {
          const res = await fetch(this.BACKEND, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              conversation_id: sessionStorage.getItem('shopAiConversationId'),
              name, phone, email,
              consent_sms: consent,
              consent_text: this.consentText,
              hooks: [hook],
              attribution: Object.assign({}, ShopAIChat.Attribution.get(), ShopAIChat.Cookies.collect()),
            })
          });
          const data = await res.json();
          if (data.ok) {
            form.classList.add('shop-ai-lead-done');
            try {
              sessionStorage.removeItem('shopAiPendingLeadHook');
              // Captured-state must survive reloads too, or the picks chip / a model
              // re-offer re-renders a fresh form and a resubmit re-texts the shopper.
              sessionStorage.setItem('shopAiLeadDone', '1');
            } catch (e) { /* non-fatal */ }
            form.querySelectorAll('input,button').forEach((el) => { el.disabled = true; });
            status.textContent = phone ? '✓ Sent — check your texts!' : '✓ Sent — keep an eye on your inbox!';
            ShopAIChat.Analytics.push('lead_form_submitted', { hook: hook });
            // Reveal gated quiz results (no-op when no gate is active).
            ShopAIChat.Quiz.unlockGate();
          } else {
            btn.disabled = false; status.textContent = data.error || 'Something went wrong. Try again.';
          }
        } catch (err) {
          btn.disabled = false; status.textContent = 'Network error — please try again.';
        }
      }
    },

    /**
     * Attention nudge: a small dismissible "speech bubble" near the launcher that
     * CYCLES through enticing messages to invite the shopper to chat. First message
     * ~5s after load; each shows ~8s then hides ~20s before the next. Keeps looping
     * while ignored; stops for the session on ✕ dismiss or when the chat is opened.
     * Static copy, no PII.
     */
    Nudge: {
      START_DELAY_MS: 2500,
      SHOW_MS: 8000,
      GAP_MS: 20000,
      MESSAGES: [
        "Take the 60-second sleep quiz — I'll find your match 🛏️",
        "Not sure which mattress is right for you? Ask me — it's free 👋",
        "Tell me how you sleep and I'll find your dream bed 🛏️",
        "Sleeping hot or waking up sore? Let's fix that 💬",
        "Delivery, sizes, prices — ask me anything ⚡",
        "Better sleep is one quick question away. Let's chat 🌙",
      ],
      el: null,
      textEl: null,
      _i: 0,
      _timer: null,
      _stopped: false,

      init: function(container) {
        if (!container) return;
        // If the shopper already dismissed or engaged this session, don't loop again.
        if (sessionStorage.getItem('shopAiNudgeStopped') === '1') return;

        // PDP: lead with a product-aware invite, then continue the generic rotation.
        const pdpTitle = ShopAIChat.PageContext.handle() && ShopAIChat.PageContext.shortTitle();
        if (pdpTitle) {
          this.MESSAGES = ["Questions about the " + pdpTitle + "? Ask me — I'll compare it to anything in the store 🛏️"].concat(this.MESSAGES);
        }

        const nudge = document.createElement('div');
        nudge.className = 'shop-ai-nudge';
        nudge.setAttribute('role', 'button');
        nudge.setAttribute('tabindex', '0');
        nudge.setAttribute('aria-label', 'Open chat');

        const text = document.createElement('span');
        text.className = 'shop-ai-nudge-text';

        const close = document.createElement('button');
        close.className = 'shop-ai-nudge-close';
        close.setAttribute('type', 'button');
        close.setAttribute('aria-label', 'Dismiss');
        close.textContent = '×';

        nudge.appendChild(text);
        nudge.appendChild(close);
        container.appendChild(nudge);
        this.el = nudge;
        this.textEl = text;

        const open = () => {
          this.dismiss();
          const win = ShopAIChat.UI.elements && ShopAIChat.UI.elements.chatWindow;
          if (!win || !win.classList.contains('active')) ShopAIChat.UI.toggleChatWindow();
        };
        nudge.addEventListener('click', (e) => { if (e.target === close) return; open(); });
        nudge.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
        });
        close.addEventListener('click', (e) => { e.stopPropagation(); this.dismiss(); });

        this._timer = setTimeout(() => this.cycle(), this.START_DELAY_MS);
      },

      // Show the next message, hide it after SHOW_MS, then loop again after GAP_MS.
      cycle: function() {
        if (this._stopped || !this.el) return;
        // Never pop over an open chat — skip this beat, re-check after the gap.
        const win = ShopAIChat.UI.elements && ShopAIChat.UI.elements.chatWindow;
        if (win && win.classList.contains('active')) {
          this._timer = setTimeout(() => this.cycle(), this.GAP_MS);
          return;
        }
        const msg = this.MESSAGES[this._i % this.MESSAGES.length];
        this.textEl.textContent = msg;
        this.el.setAttribute('aria-label', 'Open chat — ' + msg);
        this._i++;
        this.el.classList.add('active');
        this._timer = setTimeout(() => {
          if (this.el) this.el.classList.remove('active');
          if (!this._stopped) {
            this._timer = setTimeout(() => this.cycle(), this.GAP_MS);
          }
        }, this.SHOW_MS);
      },

      // Stop the loop for this page load (in memory).
      stop: function() {
        this._stopped = true;
        if (this._timer) { clearTimeout(this._timer); this._timer = null; }
        if (this.el) this.el.classList.remove('active');
      },

      // Explicit dismiss (✕) or engagement (chat opened) → stop for the whole session.
      dismiss: function() {
        sessionStorage.setItem('shopAiNudgeStopped', '1');
        this.stop();
      },
    },

    Quiz: {
      _count: 0,
      _recsFired: false,
      // Quiz-finale gate: results render blurred until the lead form is submitted.
      _gateActive: false,

      unlockGate: function() {
        if (!this._gateActive) return;
        this._gateActive = false;
        document.querySelectorAll('.shop-ai-gated').forEach((el) => el.classList.remove('shop-ai-gated'));
        document.querySelectorAll('.shop-ai-gate-note').forEach((el) => el.remove());
        // Remove the locked-results teaser overlay and unwrap its .shop-ai-gate-stack so
        // the now-revealed matches sit back in the normal flow (additive cleanup).
        document.querySelectorAll('.shop-ai-lock-overlay').forEach((el) => el.remove());
        document.querySelectorAll('.shop-ai-gate-stack').forEach((stack) => {
          const parent = stack.parentNode;
          if (!parent) return;
          while (stack.firstChild) parent.insertBefore(stack.firstChild, stack);
          parent.removeChild(stack);
        });
        ShopAIChat.Analytics.push('quiz_gate_unlocked');
        ShopAIChat.UI.scrollToBottom(true);
      },

      // Send text through the existing pipeline (same idiom as product-card buttons).
      sendAsUser: function(text) {
        const input = document.querySelector('.shop-ai-chat-input input');
        const sendButton = document.querySelector('.shop-ai-chat-send');
        if (!input || !sendButton) return;
        input.value = text;
        sendButton.click();
      },

      // Welcome hero: animated merchandising welcome state — kicker + greeting + quiz CTA
      // as the hero button (PDP pages get the fit-verdict variant) plus two ghost-chip
      // starters so openers never face a blank input. Keeps the function name so all
      // call sites and the .shop-ai-quiz-chip render-once guard stay intact.
      renderWelcomeChip: function(messagesContainer) {
        if (!messagesContainer || messagesContainer.querySelector('.shop-ai-quiz-chip')) return;
        const onPdp = !!ShopAIChat.PageContext.handle();
        const self = this;

        const hero = document.createElement('div');
        hero.className = 'shop-ai-welcome-hero';

        const kicker = document.createElement('div');
        kicker.className = 'shop-ai-hero-kicker';
        kicker.textContent = 'YOUR LOCAL MATTRESS AUTHORITY';

        const title = document.createElement('div');
        title.className = 'shop-ai-hero-title';
        title.textContent = "Let's find your perfect bed.";

        const sub = document.createElement('div');
        sub.className = 'shop-ai-hero-sub';
        sub.textContent = (window.shopChatConfig && window.shopChatConfig.heroSub) ||
          "Tell me how you sleep — I'll match you with real beds at real prices.";

        hero.appendChild(kicker);
        hero.appendChild(title);
        hero.appendChild(sub);

        const mk = function(label, sendText, analyticsEvent, extraClass) {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'shop-ai-quiz-chip' + (extraClass ? ' ' + extraClass : '');
          chip.textContent = label;
          chip.addEventListener('click', () => {
            if (analyticsEvent) ShopAIChat.Analytics.push(analyticsEvent);
            self.sendAsUser(sendText);
          });
          return chip;
        };

        hero.appendChild(mk(
          onPdp ? 'Is this bed right for me?' : String.fromCodePoint(0x1F6CF) + ' Find my match — 60-second quiz',
          onPdp ? 'Is this bed right for me? Start the sleep quiz' : 'Help me find my match — start the sleep quiz',
          'quiz_started',
          'shop-ai-chip-hero'
        ));

        const chipsRow = document.createElement('div');
        chipsRow.className = 'shop-ai-hero-chips';
        chipsRow.appendChild(mk(String.fromCodePoint(0x1F4B0) + " What's on sale?", 'What are your best mattress deals right now?', 'chip_deals_clicked', 'shop-ai-chip-ghost'));
        chipsRow.appendChild(mk(String.fromCodePoint(0x1F69A) + ' Delivery & setup', 'How do delivery, setup, and old-mattress haul-away work?', 'chip_delivery_clicked', 'shop-ai-chip-ghost'));
        hero.appendChild(chipsRow);

        messagesContainer.appendChild(hero);
      },

      removeWelcomeChip: function() {
        document.querySelectorAll('.shop-ai-quiz-chip, .shop-ai-welcome-hero, .shop-ai-text-picks-chip').forEach((el) => el.remove());
      },

      // Render one question with tappable option chips.
      show: function(data, messagesContainer) {
        if (!messagesContainer) return;
        // Dedupe: if the model repeats the SAME question (server guard should prevent
        // this, but belt-and-suspenders), don't render a second identical card.
        const lastCard = messagesContainer.querySelector('.shop-ai-quiz-card:last-of-type .shop-ai-quiz-question');
        if (lastCard && lastCard.textContent === (data.question || '')) return;
        // Only one active card: retire any previous card's buttons first.
        messagesContainer.querySelectorAll('.shop-ai-quiz-card:not(.answered)').forEach((card) => {
          card.classList.add('answered');
          card.querySelectorAll('button').forEach((b) => { b.disabled = true; });
        });
        this._count += 1;
        ShopAIChat.Analytics.push('quiz_question_shown', { step: this._count });
        const card = document.createElement('div');
        card.className = 'shop-ai-quiz-card';
        const step = document.createElement('div');
        step.className = 'shop-ai-quiz-step';
        step.textContent = 'QUESTION ' + this._count;
        card.appendChild(step);
        const q = document.createElement('div');
        q.className = 'shop-ai-quiz-question';
        q.textContent = data.question || '';
        card.appendChild(q);
        const opts = document.createElement('div');
        opts.className = 'shop-ai-quiz-options';
        (data.options || []).forEach((label) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'shop-ai-quiz-option';
          b.textContent = label;
          b.addEventListener('click', () => {
            if (card.classList.contains('answered')) return;
            card.classList.add('answered');
            b.classList.add('selected');
            card.querySelectorAll('button').forEach((x) => { x.disabled = true; });
            ShopAIChat.Quiz.sendAsUser(label);
          });
          opts.appendChild(b);
        });
        card.appendChild(opts);
        messagesContainer.appendChild(card);
        ShopAIChat.UI.scrollToBottom(true);
      },

      // quiz_recs_shown: first product_results after at least one quiz question.
      onProductResults: function() {
        if (this._count > 0 && !this._recsFired) {
          this._recsFired = true;
          ShopAIChat.Analytics.push('quiz_recs_shown', { questions: this._count });
        }
      },
    },

    Support: {
      BACKEND: 'https://mo-shop-chat-agent-production.up.railway.app/support',
      show: function(category, contact, messagesContainer) {
        if (messagesContainer.querySelector('.shop-ai-support-form')) return;
        const c = contact || {};
        const form = document.createElement('form');
        form.className = 'shop-ai-lead-form shop-ai-support-form';
        form.innerHTML =
          '<div class="shop-ai-lead-title">How can our team help?</div>' +
          '<textarea class="shop-ai-support-issue" rows="3" placeholder="Briefly describe your issue (order, delivery, return, etc.)"></textarea>' +
          '<input class="shop-ai-lead-name" type="text" placeholder="Name (optional)" autocomplete="name">' +
          '<input class="shop-ai-lead-phone" type="tel" placeholder="Phone" autocomplete="tel">' +
          '<input class="shop-ai-lead-email" type="email" placeholder="Email" autocomplete="email">' +
          '<button type="submit" class="shop-ai-lead-submit">Send to our team</button>' +
          '<div class="shop-ai-lead-status" role="status"></div>' +
          '<div class="shop-ai-support-contact"></div>';
        const contactParts = [c.email, c.phone, c.hours].filter(Boolean);
        const contactEl = form.querySelector('.shop-ai-support-contact');
        if (contactParts.length) {
          contactEl.textContent = 'Or reach us directly: ' + contactParts.join(' · ');
        } else {
          contactEl.style.display = 'none';
        }
        form.dataset.category = category || 'other';
        form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(form); });
        messagesContainer.appendChild(form);
        ShopAIChat.UI.scrollToBottom(true);
      },
      submit: async function(form) {
        const issue = form.querySelector('.shop-ai-support-issue').value.trim();
        const name = form.querySelector('.shop-ai-lead-name').value.trim();
        const phone = form.querySelector('.shop-ai-lead-phone').value.trim();
        const email = form.querySelector('.shop-ai-lead-email').value.trim();
        const status = form.querySelector('.shop-ai-lead-status');
        const btn = form.querySelector('.shop-ai-lead-submit');
        if (!issue) { status.textContent = 'Please describe your issue.'; return; }
        if (!phone && !email) { status.textContent = 'Add a phone or email so we can reach you.'; return; }
        btn.disabled = true; status.textContent = 'Sending…';
        try {
          const res = await fetch(this.BACKEND, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              conversation_id: sessionStorage.getItem('shopAiConversationId'),
              name, phone, email, issue, category: form.dataset.category,
              attribution: Object.assign({}, ShopAIChat.Attribution.get(), ShopAIChat.Cookies.collect()),
            }),
          });
          const data = await res.json();
          if (data.ok) {
            form.querySelectorAll('input,textarea,button').forEach((el) => { el.disabled = true; });
            status.textContent = '✓ Sent — our team will follow up shortly.';
            ShopAIChat.Analytics.push('support_form_submitted');
          } else { btn.disabled = false; status.textContent = data.error || 'Something went wrong. Try again.'; }
        } catch (err) { btn.disabled = false; status.textContent = 'Network error — please try again.'; }
      }
    },

    /**
     * API communication and data handling
     */
    API: {
      /**
       * Stream a response from the API
       * @param {string} userMessage - User's message text
       * @param {string} conversationId - Conversation ID for context
       * @param {HTMLElement} messagesContainer - The messages container
       */
      streamResponse: async function(userMessage, conversationId, messagesContainer) {
        let currentMessageElement = null;

        try {
          const promptType = window.shopChatConfig?.promptType || "standardAssistant";
          const requestBody = JSON.stringify({
            message: userMessage,
            conversation_id: conversationId,
            prompt_type: promptType,
            // Cookie-derived ids are merged fresh on every send (e.g. _fbc can
            // appear after first touch); stored first-touch click ids win nothing
            // here because keys don't overlap.
            attribution: Object.assign({}, ShopAIChat.Attribution.get(), ShopAIChat.Cookies.collect()),
            page: ShopAIChat.PageContext.get()
          });

          try {
            if (!sessionStorage.getItem('shopAiFirstMsgSent')) {
              sessionStorage.setItem('shopAiFirstMsgSent', '1');
              ShopAIChat.Analytics.push('chat_first_message');
            }
          } catch (e) { /* non-fatal */ }

          const streamUrl = 'https://mo-shop-chat-agent-production.up.railway.app/chat';
          const shopId = window.shopId;

          const response = await fetch(streamUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream',
              'X-Shopify-Shop-Id': shopId
            },
            body: requestBody
          });

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          // Create initial message element
          let messageElement = document.createElement('div');
          messageElement.classList.add('shop-ai-message', 'assistant');
          messageElement.textContent = '';
          messageElement.dataset.rawText = '';
          messagesContainer.appendChild(messageElement);
          currentMessageElement = messageElement;

          // Process the stream
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  this.handleStreamEvent(data, currentMessageElement, messagesContainer, userMessage,
                    (newElement) => { currentMessageElement = newElement; });
                } catch (e) {
                  console.error('Error parsing event data:', e, line);
                }
              }
            }
          }
          // Safety net: if the stream ended without an explicit end_turn/message_complete
          // (e.g. the server dropped the connection mid-reply), still release the composer.
          ShopAIChat.UI.setBusy(false);
        } catch (error) {
          console.error('Error in streaming:', error);
          ShopAIChat.UI.removeTypingIndicator();
          ShopAIChat.UI.setBusy(false);
          ShopAIChat.Message.add("Sorry, I couldn't process your request. Please try again later.",
            'assistant', messagesContainer);
        }
      },

      /**
       * Handle stream events from the API
       * @param {Object} data - Event data
       * @param {HTMLElement} currentMessageElement - Current message element being updated
       * @param {HTMLElement} messagesContainer - The messages container
       * @param {string} userMessage - The original user message
       * @param {Function} updateCurrentElement - Callback to update the current element reference
       */
      handleStreamEvent: function(data, currentMessageElement, messagesContainer, userMessage, updateCurrentElement) {
        switch (data.type) {
          case 'id':
            if (data.conversation_id) {
              sessionStorage.setItem('shopAiConversationId', data.conversation_id);
            }
            break;

          case 'chunk': {
            ShopAIChat.UI.removeTypingIndicator();
            const ts = messagesContainer.querySelector('.shop-ai-tool-status');
            if (ts) { if (ts._moSpin) clearInterval(ts._moSpin); ts.remove(); }
            currentMessageElement.dataset.rawText += data.chunk;
            // While the quiz gate is armed, any text still streaming in is finale content —
            // keep it behind the blur until the lead form unlocks it.
            if (ShopAIChat.Quiz._gateActive) currentMessageElement.classList.add('shop-ai-gated');
            ShopAIChat.Formatting.formatMessageContent(currentMessageElement);
            ShopAIChat.UI.scrollToBottom();
            break;
          }

          case 'message_complete':
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.UI.setBusy(false);
            ShopAIChat.Formatting.formatMessageContent(currentMessageElement);
            ShopAIChat.UI.scrollToBottom();
            break;

          case 'end_turn': {
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.UI.setBusy(false);
            const tsEnd = messagesContainer.querySelector('.shop-ai-tool-status');
            if (tsEnd) { if (tsEnd._moSpin) clearInterval(tsEnd._moSpin); tsEnd.remove(); }
            messagesContainer.querySelectorAll('.shop-ai-message.assistant').forEach((el) => {
              if ((el.dataset.rawText || '').trim() === '' && el.textContent.trim() === '') el.remove();
            });
            break;
          }

          case 'error':
            console.error('Stream error:', data.error);
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.UI.setBusy(false);
            currentMessageElement.textContent = "Sorry, I couldn't process your request. Please try again later.";
            break;

          case 'rate_limit_exceeded':
            console.error('Rate limit exceeded:', data.error);
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.UI.setBusy(false);
            currentMessageElement.textContent = "Sorry, our servers are currently busy. Please try again later.";
            break;

          case 'auth_required':
            // Save the last user message for resuming after authentication
            sessionStorage.setItem('shopAiLastMessage', userMessage || '');
            break;

          case 'product_results':
            ShopAIChat.UI.displayProductResults(data.products);
            ShopAIChat.Quiz.onProductResults();
            break;

          case 'tool_use': {
            ShopAIChat.UI.removeTypingIndicator();
            const prevStatus = messagesContainer.querySelector('.shop-ai-tool-status');
            if (prevStatus) { if (prevStatus._moSpin) clearInterval(prevStatus._moSpin); prevStatus.remove(); }
            const sleepVerbs = [
              'Fluffing the pillows… 🛏️',
              'Counting sheep… 🐑',
              'Dreaming up the comfiest picks… 💤',
              'Plumping the cushions… ☁️',
              'Smoothing the sheets… ✨',
              'Checking the comfort levels… 🌙',
              'Tucking in the details… 😴'
            ];
            const statusEl = document.createElement('div');
            statusEl.className = 'shop-ai-tool-status';
            let moVerbIndex = Math.floor(Math.random() * sleepVerbs.length);
            statusEl.textContent = sleepVerbs[moVerbIndex];
            statusEl._moSpin = setInterval(function () {
              moVerbIndex = (moVerbIndex + 1) % sleepVerbs.length;
              statusEl.textContent = sleepVerbs[moVerbIndex];
            }, 1700);
            messagesContainer.appendChild(statusEl);
            ShopAIChat.UI.scrollToBottom();
            break;
          }

          case 'request_lead': {
            ShopAIChat.UI.removeTypingIndicator();
            // Quiz-finale gate: the offer arriving between the last quiz answer and the
            // first recs marks the finale. Gate only if the form actually rendered (the
            // already-captured guards inside Lead.show can no-op it — never gate without
            // a working unlock path).
            const quizFinale = ShopAIChat.Quiz._count > 0 && !ShopAIChat.Quiz._recsFired;
            ShopAIChat.Lead.show(data.hook || 'save_cart', messagesContainer);
            if (quizFinale &&
                messagesContainer.querySelector('form.shop-ai-lead-form:not(.shop-ai-support-form):not(.shop-ai-lead-done)')) {
              ShopAIChat.Quiz._gateActive = true;
              // The finale recs can span MULTIPLE assistant bubbles (prose pass + post-form
              // acknowledgment pass) — gate every assistant bubble after the shopper's last
              // message, not just the current one. Streaming after this point is gated in
              // the 'chunk' handler until unlockGate().
              const msgs = Array.from(messagesContainer.querySelectorAll('.shop-ai-message'));
              let lastUserIdx = -1;
              msgs.forEach((m, i) => { if (m.classList.contains('user')) lastUserIdx = i; });
              msgs.forEach((m, i) => {
                if (i > lastUserIdx && m.classList.contains('assistant')) m.classList.add('shop-ai-gated');
              });
              if (currentMessageElement) currentMessageElement.classList.add('shop-ai-gated');
              ShopAIChat.Analytics.push('quiz_gate_shown');
            }
            break;
          }

          case 'request_support':
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.Support.show(data.category || 'other', data.contact || {}, messagesContainer);
            break;

          case 'quiz_question':
            ShopAIChat.UI.removeTypingIndicator();
            // Quiz turns should be chip-only. If the model leaked stray prose before the
            // chip (the "thinking block" glitch), clear that in-progress bubble so the
            // shopper sees just the question card. (end_turn prunes the now-empty bubble.)
            if (currentMessageElement) {
              currentMessageElement.dataset.rawText = '';
              currentMessageElement.textContent = '';
            }
            ShopAIChat.Quiz.show(data, messagesContainer);
            break;

          case 'new_message':
            ShopAIChat.Formatting.formatMessageContent(currentMessageElement);
            ShopAIChat.UI.showTypingIndicator();

            // Create new message element for the next response
            const newMessageElement = document.createElement('div');
            newMessageElement.classList.add('shop-ai-message', 'assistant');
            newMessageElement.textContent = '';
            newMessageElement.dataset.rawText = '';
            messagesContainer.appendChild(newMessageElement);

            // Update the current element reference
            updateCurrentElement(newMessageElement);
            break;

          case 'content_block_complete':
            ShopAIChat.UI.showTypingIndicator();
            break;
        }
      },

      /**
       * Fetch chat history from the server
       * @param {string} conversationId - Conversation ID
       * @param {HTMLElement} messagesContainer - The messages container
       */
      fetchChatHistory: async function(conversationId, messagesContainer) {
        try {
          // Show a loading message
          const loadingMessage = document.createElement('div');
          loadingMessage.classList.add('shop-ai-message', 'assistant');
          loadingMessage.textContent = "Loading conversation history...";
          messagesContainer.appendChild(loadingMessage);

          // Fetch history from the server
          const historyUrl = `https://mo-shop-chat-agent-production.up.railway.app/chat?history=true&conversation_id=${encodeURIComponent(conversationId)}`;
          console.log('Fetching history from:', historyUrl);

          const response = await fetch(historyUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            mode: 'cors'
          });

          if (!response.ok) {
            console.error('History fetch failed:', response.status, response.statusText);
            throw new Error('Failed to fetch chat history: ' + response.status);
          }

          const data = await response.json();

          // Remove loading message
          messagesContainer.removeChild(loadingMessage);

          // No messages: fresh thread — show the welcome hero (it carries the greeting).
          if (!data.messages || data.messages.length === 0) {
            try { sessionStorage.removeItem('shopAiPendingLeadHook'); } catch (e) { /* non-fatal */ }
            ShopAIChat.Quiz.renderWelcomeChip(messagesContainer);
            return;
          }

          // Add messages to the UI - filter out tool results
          data.messages.forEach(message => {
            try {
              const messageContents = JSON.parse(message.content);
              for (const contentBlock of messageContents) {
                if (contentBlock.type === 'text') {
                  ShopAIChat.Message.add(contentBlock.text, message.role, messagesContainer);
                }
              }
            } catch (e) {
              ShopAIChat.Message.add(message.content, message.role, messagesContainer);
            }
          });

          // An unsubmitted lead form must survive page reloads (every product-card click is
          // a full reload while the transcript still says "pop your details into the box").
          try {
            const pendingHook = sessionStorage.getItem('shopAiPendingLeadHook');
            if (pendingHook) ShopAIChat.Lead.show(pendingHook, messagesContainer, { restored: true });
          } catch (e) { /* non-fatal */ }

          // Scroll to bottom
          ShopAIChat.UI.scrollToBottom(true);

        } catch (error) {
          console.error('Error fetching chat history:', error);

          // Remove loading message if it exists
          const loadingMessage = messagesContainer.querySelector('.shop-ai-message.assistant');
          if (loadingMessage && loadingMessage.textContent === "Loading conversation history...") {
            messagesContainer.removeChild(loadingMessage);
          }

          // Show the welcome hero (it carries the greeting) and start fresh
          try { sessionStorage.removeItem('shopAiPendingLeadHook'); } catch (e2) { /* non-fatal */ }
          ShopAIChat.Quiz.renderWelcomeChip(messagesContainer);

          // Clear the conversation ID since we couldn't fetch this conversation
          sessionStorage.removeItem('shopAiConversationId');
        }
      }
    },

    /**
     * Authentication-related functionality
     */
    Auth: {
      /**
       * Opens an authentication popup window
       * @param {string|HTMLElement} authUrlOrElement - The auth URL or link element that was clicked
       */
      openAuthPopup: function(authUrlOrElement) {
        let authUrl;
        if (typeof authUrlOrElement === 'string') {
          // If a string URL was passed directly
          authUrl = authUrlOrElement;
        } else {
          // If an element was passed
          authUrl = authUrlOrElement.getAttribute('data-auth-url');
          if (!authUrl) {
            console.error('No auth URL found in element');
            return;
          }
        }

        // Open the popup window centered in the screen
        const width = 600;
        const height = 700;
        const left = (window.innerWidth - width) / 2 + window.screenX;
        const top = (window.innerHeight - height) / 2 + window.screenY;

        const popup = window.open(
          authUrl,
          'ShopifyAuth',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        // Focus the popup window
        if (popup) {
          popup.focus();
        } else {
          // If popup was blocked, show a message
          alert('Please allow popups for this site to authenticate with Shopify.');
        }

        // Start polling for token availability
        const conversationId = sessionStorage.getItem('shopAiConversationId');
        if (conversationId) {
          const messagesContainer = document.querySelector('.shop-ai-chat-messages');

          // Add a message to indicate authentication is in progress
          ShopAIChat.Message.add("Authentication in progress. Please complete the process in the popup window.",
            'assistant', messagesContainer);

          this.startTokenPolling(conversationId, messagesContainer);
        }
      },

      /**
       * Start polling for token availability
       * @param {string} conversationId - Conversation ID
       * @param {HTMLElement} messagesContainer - The messages container
       */
      startTokenPolling: function(conversationId, messagesContainer) {
        if (!conversationId) return;

        console.log('Starting token polling for conversation:', conversationId);
        const pollingId = 'polling_' + Date.now();
        sessionStorage.setItem('shopAiTokenPollingId', pollingId);

        let attemptCount = 0;
        const maxAttempts = 30;

        const poll = async () => {
          if (sessionStorage.getItem('shopAiTokenPollingId') !== pollingId) {
            console.log('Another polling session has started, stopping this one');
            return;
          }

          if (attemptCount >= maxAttempts) {
            console.log('Max polling attempts reached, stopping');
            return;
          }

          attemptCount++;

          try {
            const tokenUrl = 'https://mo-shop-chat-agent-production.up.railway.app/auth/token-status?conversation_id=' +
              encodeURIComponent(conversationId);
            const response = await fetch(tokenUrl);

            if (!response.ok) {
              throw new Error('Token status check failed: ' + response.status);
            }

            const data = await response.json();

            if (data.status === 'authorized') {
              console.log('Token available, resuming conversation');
              const message = sessionStorage.getItem('shopAiLastMessage');

              if (message) {
                sessionStorage.removeItem('shopAiLastMessage');
                setTimeout(() => {
                  ShopAIChat.Message.add("Authorization successful! I'm now continuing with your request.",
                    'assistant', messagesContainer);
                  ShopAIChat.API.streamResponse(message, conversationId, messagesContainer);
                  ShopAIChat.UI.showTypingIndicator();
                }, 500);
              }

              sessionStorage.removeItem('shopAiTokenPollingId');
              return;
            }

            console.log('Token not available yet, polling again in 10s');
            setTimeout(poll, 10000);
          } catch (error) {
            console.error('Error polling for token status:', error);
            setTimeout(poll, 10000);
          }
        };

        setTimeout(poll, 2000);
      }
    },

    /**
     * Product-related functionality
     */
    Product: {
      /**
       * Transient add-to-cart feedback (ADDITIVE, purely visual).
       * Cycles the button label "Adding…" → "Added ✓" and toggles state classes,
       * then reverts to the original label after ~1.6s. Self-contained: stores the
       * original label, ignores re-entrant clicks while mid-flash, and never throws.
       * @param {HTMLButtonElement} button
       */
      flashAddToCart: function(button) {
        try {
          if (!button || button.dataset.moFlashing === '1') return;
          const original = button.dataset.moLabel || button.textContent || 'Add to Cart';
          button.dataset.moLabel = original;
          button.dataset.moFlashing = '1';
          if (button._moFlashT1) clearTimeout(button._moFlashT1);
          if (button._moFlashT2) clearTimeout(button._moFlashT2);

          button.classList.remove('is-added');
          button.classList.add('is-loading');
          button.textContent = 'Adding…';

          button._moFlashT1 = setTimeout(function () {
            button.classList.remove('is-loading');
            button.classList.add('is-added');
            button.textContent = 'Added ✓';
          }, 520);

          button._moFlashT2 = setTimeout(function () {
            button.classList.remove('is-loading', 'is-added');
            button.textContent = button.dataset.moLabel || original;
            button.dataset.moFlashing = '0';
          }, 1600);
        } catch (e) { /* non-fatal */ }
      },

      /**
       * Parse the bot's per-bed reply lines into {name, reason} pairs. The RESPONSE STYLE
       * contract emits one dash line per bed: "- **Brand Model** — $price — plain reason."
       * We take the clause AFTER the price as the reason. Pure, defensive, never throws.
       * @param {string} replyText - the assistant's raw reply markdown
       * @returns {Array<{name:string, reason:string}>}
       */
      extractReasons: function(replyText) {
        const out = [];
        if (!replyText) return out;
        // em-dash / en-dash / space-padded hyphen all act as the segment separator
        const SEP = /\s[—–-]\s/;
        String(replyText).split('\n').forEach(function(raw) {
          const line = String(raw).replace(/^\s*[-*•]\s+/, '').trim();
          const bold = line.match(/\*\*(.+?)\*\*/);
          if (!bold) return;
          const segs = line.split(SEP);
          if (segs.length < 3) return; // need name — price — reason
          let reason = segs.slice(2).join(' — ')
            .replace(/\*\*/g, '').replace(/\s+/g, ' ').replace(/[\s.;,]+$/, '').trim();
          if (reason.length < 3) return;
          if (reason.length > 90) reason = reason.slice(0, 88).replace(/\s+\S*$/, '') + '…';
          out.push({ name: bold[1].replace(/\*/g, '').trim(), reason: reason });
        });
        return out;
      },

      /**
       * Match a product title to its reason from extractReasons(), mirroring the server's
       * name-matching (selectRecommendedProducts): a bolded name and the title match when
       * one contains the other or they share a 2-word run. Returns the reason or null.
       * @param {string} title
       * @param {Array<{name:string, reason:string}>} reasons
       * @returns {string|null}
       */
      reasonFor: function(title, reasons) {
        if (!title || !Array.isArray(reasons) || !reasons.length) return null;
        const norm = function(s) {
          return String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
        };
        const t = norm(title);
        if (!t) return null;
        for (let i = 0; i < reasons.length; i++) {
          const n = norm(reasons[i].name);
          if (!n) continue;
          if (t.indexOf(n) !== -1 || n.indexOf(t) !== -1) return reasons[i].reason;
          const w = n.split(' ').filter(Boolean);
          for (let j = 0; j < w.length - 1; j++) {
            if (t.indexOf(w[j] + ' ' + w[j + 1]) !== -1) return reasons[i].reason;
          }
        }
        return null;
      },

      /**
       * Create a product card element
       * @param {Object} product - Product data
       * @param {string} [reason] - optional one-line "why it fits" note from the bot's reply
       * @returns {HTMLElement} Product card element
       */
      createCard: function(product, reason) {
        const card = document.createElement('div');
        card.classList.add('shop-ai-product-card');

        // Whole-card click → PDP (bigger tap target than the title link alone). Early-return
        // for the add-to-cart button and real links so their own handlers keep working.
        if (product.url) {
          card.classList.add('clickable');
          card.addEventListener('click', function(e) {
            if (e.target.closest('.shop-ai-add-to-cart') || e.target.closest('a')) return;
            window.open(ShopAIChat.Formatting.safeUrl(product.url), '_blank', 'noopener');
          });
        }

        // Create image container
        const imageContainer = document.createElement('div');
        imageContainer.classList.add('shop-ai-product-image');

        // Add product image (only http(s) URLs are allowed; otherwise placeholder)
        const PLACEHOLDER_IMG = 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
        const image = document.createElement('img');
        image.src = /^https?:\/\//i.test(product.image_url || '') ? product.image_url : PLACEHOLDER_IMG;
        image.alt = product.title || 'Product';
        image.onerror = function() {
          this.src = PLACEHOLDER_IMG;
        };
        imageContainer.appendChild(image);
        card.appendChild(imageContainer);

        // Add product info
        const info = document.createElement('div');
        info.classList.add('shop-ai-product-info');

        // Add product title
        const title = document.createElement('h3');
        title.classList.add('shop-ai-product-title');
        title.textContent = product.title;

        // If product has a URL, make the title a link
        if (product.url) {
          const titleLink = document.createElement('a');
          titleLink.href = ShopAIChat.Formatting.safeUrl(product.url);
          titleLink.target = '_blank';
          titleLink.rel = 'noopener noreferrer';
          titleLink.textContent = product.title;
          title.textContent = '';
          title.appendChild(titleLink);
        }

        info.appendChild(title);

        // "Why it fits you" rationale, lifted verbatim from the bot's reply (textContent
        // only — grounding-safe). Rendered only when a reason was matched for this bed.
        if (reason) {
          const reasonEl = document.createElement('p');
          reasonEl.classList.add('shop-ai-product-reason');
          reasonEl.textContent = reason;
          info.appendChild(reasonEl);
        }

        // Add product price. "from $X" gets split typography (small "from", big price);
        // the combined textContent stays byte-identical to product.price — grounding doctrine.
        const price = document.createElement('p');
        price.classList.add('shop-ai-product-price');
        const priceStr = product.price == null ? '' : String(product.price);
        if (/^from /.test(priceStr)) {
          const from = document.createElement('span');
          from.className = 'shop-ai-price-from';
          from.textContent = 'from ';
          const val = document.createElement('span');
          val.className = 'shop-ai-price-value';
          val.textContent = priceStr.slice(5);
          price.appendChild(from);
          price.appendChild(val);
        } else {
          price.textContent = priceStr;
        }
        info.appendChild(price);

        // Add add-to-cart button
        const button = document.createElement('button');
        button.classList.add('shop-ai-add-to-cart');
        button.textContent = 'Add to Cart';
        button.dataset.productId = product.id;

        // Add click handler for the button
        button.addEventListener('click', function() {
          // Add-to-cart routes through the chat send button. While a reply is streaming the
          // send button is disabled (busy state), so a click would no-op — bail BEFORE the
          // visual flash so we never show a misleading "Added ✓" without an actual action.
          const sendButton = document.querySelector('.shop-ai-chat-send');
          if (sendButton && sendButton.disabled) return;

          // Transient feedback (ADDITIVE): "Adding…" → "Added ✓" on the button itself,
          // reverting after ~1.6s. Purely visual — the existing add-to-cart behavior
          // (route the request through the chat) is unchanged below.
          ShopAIChat.Product.flashAddToCart(button);

          // Send message to add this product to cart
          const input = document.querySelector('.shop-ai-chat-input input');
          if (input) {
            input.value = `Add ${product.title} to my cart`;
            // Trigger a click on the send button
            if (sendButton) {
              sendButton.click();
            }
          }
        });

        info.appendChild(button);
        card.appendChild(info);

        return card;
      }
    },

    /**
     * Initialize the chat application
     */
    init: function() {
      // Capture first-touch ad attribution (gclid/fbclid/utm) before anything else
      ShopAIChat.Attribution.capture();

      // Initialize UI
      const container = document.querySelector('.shop-ai-chat-container');
      if (!container) return;

      this.UI.init(container);
      this.Nudge.init(container);

      // Check for existing conversation
      const conversationId = sessionStorage.getItem('shopAiConversationId');

      if (conversationId) {
        // Fetch conversation history
        this.API.fetchChatHistory(conversationId, this.UI.elements.messagesContainer);
      } else {
        // No previous conversation: the welcome hero carries the greeting
        try { sessionStorage.removeItem('shopAiPendingLeadHook'); } catch (e) { /* non-fatal */ }
        this.Quiz.renderWelcomeChip(this.UI.elements.messagesContainer);
      }
    }
  };

  // Initialize the application when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    ShopAIChat.init();
  });
})();
